/**
 * Five Vibe presets — Experience 7-axis + token bundle per spec
 */

import type {
  SiteDesignManifest,
  SiteExperienceGuide,
  SiteVibeId,
} from '@shared/site/types/site-design-manifest';
import { componentFromSemantic } from '@shared/site/design/token-tier-registry';

export interface VibePresetMeta {
  id: SiteVibeId;
  labelKo: string;
  targetKo: string;
  swatches: string[];
  axisSummaryKo: string;
}

export const VIBE_PRESET_META: VibePresetMeta[] = [
  {
    id: 'minimal_pro',
    labelKo: 'Minimal Pro',
    targetKo: 'SaaS · 전문서비스 · 컨설팅',
    swatches: ['#1e293b', '#3b82f6', '#f8fafc', '#64748b'],
    axisSummaryKo: '신뢰·정밀 · Open · Geometric Sans · Subtle',
  },
  {
    id: 'bold_impact',
    labelKo: 'Bold Impact',
    targetKo: '스타트업 · 브랜드 · 이커머스',
    swatches: ['#0f172a', '#eab308', '#f97316', '#1e293b'],
    axisSummaryKo: '에너지·자신감 · Compressed · Display · Dynamic',
  },
  {
    id: 'warm_artisan',
    labelKo: 'Warm Artisan',
    targetKo: '식음 · 숙박 · 수공예 · 로컬',
    swatches: ['#78350f', '#d97706', '#fef3c7', '#92400e'],
    axisSummaryKo: '따뜻함·진정성 · Balanced · Humanist · Expressive',
  },
  {
    id: 'tech_native',
    labelKo: 'Tech Native',
    targetKo: 'IT · 솔루션 · 제조 · B2B',
    swatches: ['#020617', '#0ea5e9', '#38bdf8', '#0f172a'],
    axisSummaryKo: '정확·미래지향 · Dense · Mono+Sans · Snappy',
  },
  {
    id: 'premium_heritage',
    labelKo: 'Premium Heritage',
    targetKo: '명품 · 금융 · 법률 · 전통',
    swatches: ['#1c1917', '#a16207', '#fef9c3', '#292524'],
    axisSummaryKo: '전통·권위 · Open · Classic Serif · Slow',
  },
];

function primitiveBundle(
  primary: string,
  secondary: string,
  surface: string,
  onSurface: string,
  accent: string,
  displayFont: string,
  bodyFont: string
): Record<string, string> {
  return {
    '--color-brand-primary': primary,
    '--color-brand-secondary': secondary,
    '--color-brand-surface': surface,
    '--color-brand-on-surface': onSurface,
    '--color-brand-accent': accent,
    '--font-display': displayFont,
    '--font-body': bodyFont,
  };
}

function semanticFromPrimitive(primitive: Record<string, string>): Record<string, string> {
  return {
    '--color-accent': 'var(--color-brand-accent)',
    '--color-surface': 'var(--color-brand-surface)',
    '--color-on-surface': 'var(--color-brand-on-surface)',
    '--color-primary': 'var(--color-brand-primary)',
    '--section-gap-sm': 'calc(0.5rem * var(--site-density-scale, 1))',
    '--section-gap-md': 'calc(1rem * var(--site-density-scale, 1))',
    '--section-gap-lg': 'calc(2rem * var(--site-density-scale, 1))',
    '--section-stack-gap': 'var(--section-gap-lg)',
    '--font-display': primitive['--font-display'] ?? 'system-ui, sans-serif',
    '--font-body': primitive['--font-body'] ?? 'system-ui, sans-serif',
    '--button-bg': 'var(--color-brand-primary)',
    '--card-radius': '0.5rem',
  };
}

const VIBE_EXPERIENCE: Record<SiteVibeId, SiteExperienceGuide> = {
  minimal_pro: {
    emotionAxis: 'cool_trust',
    spaceDensity: 'open',
    typographyPair: 'geometric_sans',
    motionLevel: 'subtle',
    imageStyle: 'muted',
    copyTone: 'formal',
  },
  bold_impact: {
    emotionAxis: 'hot_energy',
    spaceDensity: 'dense',
    typographyPair: 'display_heavy',
    motionLevel: 'dynamic',
    imageStyle: 'vivid',
    copyTone: 'playful',
  },
  warm_artisan: {
    emotionAxis: 'warm_authentic',
    spaceDensity: 'balanced',
    typographyPair: 'humanist_serif',
    motionLevel: 'expressive',
    imageStyle: 'warm',
    copyTone: 'warm',
  },
  tech_native: {
    emotionAxis: 'precise_future',
    spaceDensity: 'dense',
    typographyPair: 'mono_sans',
    motionLevel: 'dynamic',
    imageStyle: 'muted',
    copyTone: 'authoritative',
  },
  premium_heritage: {
    emotionAxis: 'cool_trust',
    spaceDensity: 'open',
    typographyPair: 'classic_serif',
    motionLevel: 'subtle',
    imageStyle: 'muted',
    copyTone: 'authoritative',
  },
};

const VIBE_PRIMITIVES: Record<SiteVibeId, Record<string, string>> = {
  minimal_pro: primitiveBundle(
    '#1e293b',
    '#3b82f6',
    '#f8fafc',
    '#0f172a',
    '#3b82f6',
    '"Inter", system-ui, sans-serif',
    '"Inter", system-ui, sans-serif'
  ),
  bold_impact: primitiveBundle(
    '#0f172a',
    '#eab308',
    '#1e293b',
    '#f8fafc',
    '#f97316',
    '"Archivo Black", system-ui, sans-serif',
    '"DM Sans", system-ui, sans-serif'
  ),
  warm_artisan: primitiveBundle(
    '#78350f',
    '#d97706',
    '#fef3c7',
    '#451a03',
    '#d97706',
    '"Lora", Georgia, serif',
    '"Source Sans 3", system-ui, sans-serif'
  ),
  tech_native: primitiveBundle(
    '#020617',
    '#0ea5e9',
    '#0f172a',
    '#e2e8f0',
    '#38bdf8',
    '"JetBrains Mono", monospace',
    '"IBM Plex Sans", system-ui, sans-serif'
  ),
  premium_heritage: primitiveBundle(
    '#1c1917',
    '#a16207',
    '#fef9c3',
    '#292524',
    '#a16207',
    '"Cormorant Garamond", Georgia, serif',
    '"Libre Baskerville", Georgia, serif'
  ),
};

const MOTION_BY_LEVEL: Record<
  SiteExperienceGuide['motionLevel'],
  { easing: string; durationScale: number }
> = {
  none: { easing: 'linear', durationScale: 0 },
  subtle: { easing: 'cubic-bezier(0.4, 0, 0.2, 1)', durationScale: 1 },
  expressive: { easing: 'cubic-bezier(0.4, 0, 0.2, 1)', durationScale: 1.25 },
  dynamic: { easing: 'cubic-bezier(0.2, 0.8, 0.2, 1)', durationScale: 0.75 },
};

const DENSITY_SCALE: Record<SiteExperienceGuide['spaceDensity'], number> = {
  open: 1.35,
  balanced: 1,
  dense: 0.75,
};

export const IMAGE_OVERLAY_BY_STYLE: Record<
  SiteExperienceGuide['imageStyle'],
  SiteDesignManifest['imageOverlay']
> = {
  vivid: {
    hero: { opacity: 0.35, blendMode: 'multiply' },
    context: { opacity: 0.2 },
    texture: { opacity: 0.15, blur: 0 },
  },
  muted: {
    hero: { opacity: 0.5, blendMode: 'multiply' },
    context: { opacity: 0.35 },
    texture: { opacity: 0.25, blur: 2 },
  },
  warm: {
    hero: { opacity: 0.4, blendMode: 'soft-light' },
    context: { opacity: 0.3 },
    texture: { opacity: 0.2, blur: 1 },
  },
  bw: {
    hero: { opacity: 0.55, blendMode: 'multiply' },
    context: { opacity: 0.4 },
    texture: { opacity: 0.3, blur: 3 },
  },
};

export function buildDesignManifestFromVibe(vibeId: SiteVibeId): SiteDesignManifest {
  const experienceGuide = VIBE_EXPERIENCE[vibeId];
  const primitive = { ...VIBE_PRIMITIVES[vibeId] };
  const semantic = semanticFromPrimitive(primitive);
  const motion = { ...MOTION_BY_LEVEL[experienceGuide.motionLevel] };
  const spacing = { densityScale: DENSITY_SCALE[experienceGuide.spaceDensity] };
  const imageOverlay = IMAGE_OVERLAY_BY_STYLE[experienceGuide.imageStyle];

  return {
    vibeId,
    experienceGuide: { ...experienceGuide },
    tokens: {
      primitive,
      semantic,
      component: componentFromSemantic(semantic),
      motion,
      spacing,
    },
    sectionVariants: {},
    imageOverlay: { ...imageOverlay },
  };
}

export function getDefaultDesignManifest(): SiteDesignManifest {
  return buildDesignManifestFromVibe('minimal_pro');
}

export function getVibeMeta(vibeId: SiteVibeId): VibePresetMeta {
  const meta = VIBE_PRESET_META.find((v) => v.id === vibeId);
  if (!meta) {
    throw new Error(`Unknown vibe: ${vibeId}`);
  }
  return meta;
}
